import { displayUrlDocumentWithoutDownload } from "@api";
import type { IFupLogo } from "@bbj-types/organisation";
import AddIcon from "@mui/icons-material/Add";
import CheckIcon from "@mui/icons-material/Check";
import ClearIcon from "@mui/icons-material/Clear";
import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { Divider, IconButton, Stack, Typography } from "@mui/material";
import { IconButtonVariant, MenuPopover } from "@styleguide";
import { useMemo, useState } from "react";
import { type Control, Controller, type UseFormGetValues, type UseFormSetValue } from "react-hook-form";
import type { TPartnerFeedbackFormResolver } from "./resolver";

interface ISponsorsLogoProps {
  control: Control<TPartnerFeedbackFormResolver>;
  sponsorsLogo: IFupLogo[];
  setValue: UseFormSetValue<TPartnerFeedbackFormResolver>;
  getValues: UseFormGetValues<TPartnerFeedbackFormResolver>;
}

export const SponsorsLogo = ({ control, sponsorsLogo, setValue, getValues }: ISponsorsLogoProps) => {
  const sponsorLogoField = getValues("sponsorsLogo");
  const [selectedLogos, setSelectedLogos] = useState<IFupLogo[]>(sponsorLogoField);
  const requiredLogos = useMemo(() => sponsorsLogo.filter((logo) => logo.required) ?? [], [sponsorsLogo]);
  const additionalLogos = useMemo(() => sponsorsLogo.filter((logo) => !logo.required) ?? [], [sponsorsLogo]);
  const [open, setOpen] = useState(false);

  return (
    <Stack
      sx={{
        width: "fit-content",
      }}
    >
      <Stack
        sx={{
          mb: 2,
        }}
      >
        {requiredLogos.map((rLogo) => (
          <Stack key={rLogo.value}>
            <Stack
              direction="row"
              sx={{
                justifyContent: "space-between",
                alignItems: "center",
                py: 1,
              }}
            >
              <Typography variant="body1SemiBold" color="black">
                {rLogo.label}
              </Typography>
            </Stack>
            <Divider />
          </Stack>
        ))}

        {sponsorLogoField.map((logo) => (
          <Stack key={logo.value}>
            <Stack
              direction="row"
              sx={{
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography variant="body1SemiBold" color="black">
                {logo.label}
              </Typography>
              {!logo.required && (
                <IconButton
                  size="small"
                  color="primary"
                  onClick={() => {
                    setValue(
                      "sponsorsLogo",
                      selectedLogos.filter((l) => l.value !== logo.value)
                    );
                    setSelectedLogos((logos) => logos.filter((l) => l.value !== logo.value));
                  }}
                >
                  <DeleteIcon />
                </IconButton>
              )}
            </Stack>
            <Divider />
          </Stack>
        ))}
      </Stack>
      <Controller
        control={control}
        name="sponsorsLogo"
        render={({ field: { value, onChange }, fieldState: { error } }) => (
          <MenuPopover
            label={<Typography variant="body1SemiBold">Importer un autre logo financeur</Typography>}
            variant="filter"
            ButtonProps={{ sx: { width: "fit-content", fontWeight: "700px" } }}
            open={open}
            onClick={() => setOpen(true)}
            onClose={() => {
              setOpen(false);
              setSelectedLogos(sponsorLogoField);
            }}
          >
            {additionalLogos.map((logo) => (
              <Stack
                key={logo.value}
                direction="row"
                sx={{
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Stack>{logo.label}</Stack>
                <Stack direction="row">
                  {!selectedLogos.some((sel) => sel.value === logo.value) ? (
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() => setSelectedLogos((logos) => [...logos, logo])}
                    >
                      <AddIcon fontSize="small" />
                    </IconButton>
                  ) : (
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() => setSelectedLogos((logos) => logos.filter((l) => l.value !== logo.value))}
                    >
                      <CheckIcon fontSize="small" color="success" />
                    </IconButton>
                  )}
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={() => displayUrlDocumentWithoutDownload(`${logo.uri}`, logo.label)}
                  >
                    <VisibilityIcon fontSize="small" />
                  </IconButton>
                </Stack>
              </Stack>
            ))}
            <Divider sx={{ marginBottom: 1 }} />
            <Stack
              direction="row"
              sx={{
                justifyContent: "end",
                gap: 1,
              }}
            >
              <IconButtonVariant
                variant="elevated"
                onClick={() => {
                  setOpen(false);
                  setSelectedLogos(sponsorLogoField);
                }}
              >
                <ClearIcon color="primary" />
              </IconButtonVariant>
              <IconButtonVariant
                variant="elevated"
                onClick={() => {
                  onChange(selectedLogos);
                  setOpen(false);
                }}
              >
                <CheckIcon color="success" />
              </IconButtonVariant>
            </Stack>
          </MenuPopover>
        )}
      />
    </Stack>
  );
};
